import { useT } from "../../contexts/LanguageContext";
import React, { useEffect, useMemo, useState } from "react";
import { getPresence } from "../../api/operator";
import { parseVoiceCall, stripBold, voiceCallLabel } from "./messageFormat";
import { CheckBadgeIcon } from "@heroicons/react/24/solid";

const formatTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date();
  if (
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate()
  ) {
    return d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
  }
  return d.toLocaleDateString("ru-RU", { day: "numeric", month: "short" });
};

// Маңыздылық тек жоғары/шұғыл болса, аттың жанында белгі болып шығады.
// (Бұрын аватар бұрышындағы көк нүкте еді — «бәрі онлайн» болып көрінетін.)
const PRIORITY_BADGE = {
  high: { cls: "bg-orange-500/15 text-orange-400 border-orange-500/30", key: "filter.priorityHigh" },
  urgent: { cls: "bg-red-500/15 text-red-400 border-red-500/30", key: "filter.priorityUrgent" },
};

/** Тізімдегі соңғы хабарламаның қысқаша сипаты.
 *
 *  Медиа хабарламаларда `message` не бос (жарнама), не шикі URL (аудио/файл)
 *  болады — тізімде ұзын сілтеме тұрмауы үшін түріне қарай жазу көрсетеміз. */
const previewText = (chat, t) => {
  const type = chat.last_message_type;
  if (type === "audio") return `🎤 ${t("chat.voiceMessage")}`;
  if (type === "announcement") return `📋 ${t("chat.announcement")}`;
  if (type === "location") return `📍 ${t("chat.location")}`;
  if (type === "file") return `📎 ${t("chat.file")}`;
  const body = (chat.last_message || "").trim();
  if (!body) return "...";
  // Мәтіннің өзі жалғыз сілтеме болса да — қысқартып көрсетеміз.
  if (/^https?:\/\/\S+$/.test(body)) return `🔗 ${t("chat.link")}`;
  const call = parseVoiceCall(body);
  if (call) return `📞 ${voiceCallLabel(call, t)}`;
  return stripBold(body);
};

const ChatItem = ({ chat, isActive, onClick, action, online }) => {
  const t = useT();
  const u = chat.user || {};
  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-3 border-b border-gray-800 transition-colors ${
        isActive ? "bg-bg-primary" : "hover:bg-bg-primary/40"
      }`}
    >
      <div className="flex items-start gap-3">
        <div className="relative">
          <div className="w-12 h-12 rounded-full bg-accent-grad flex items-center justify-center text-white font-semibold">
            {u.name?.[0]?.toUpperCase() || "?"}
          </div>
          {/* Нақты онлайн (сокет) — /operator/presence */}
          {online && (
            <span
              className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-bg-base bg-green-500"
              title={t("profile.onlineNow")}
            />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-1.5 min-w-0">
              <span className="text-white font-medium truncate">{u.name || t("list.user")}</span>
              {PRIORITY_BADGE[chat.priority] && (
                <span className={`shrink-0 px-1.5 py-0.5 text-[10px] rounded border ${PRIORITY_BADGE[chat.priority].cls}`}>
                  {t(PRIORITY_BADGE[chat.priority].key)}
                </span>
              )}
            </span>
            <span className="text-xs text-text-secondary shrink-0">
              {formatTime(chat.last_message_at || chat.created_at || chat.assigned_at)}
            </span>
          </div>
          <div className="flex items-center justify-between gap-2 mt-0.5">
            <span className="text-sm text-text-secondary truncate">
              {previewText(chat, t)}
            </span>
            {chat.unread_count > 0 && (
              <span className="bg-green-500 text-white text-xs rounded-full px-1.5 py-0.5 min-w-[20px] text-center shrink-0">
                {chat.unread_count}
              </span>
            )}
          </div>
          {chat.tags && chat.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {chat.tags.slice(0, 3).map((t) => (
                <span
                  key={t}
                  className="px-1.5 py-0.5 text-xs rounded bg-blue-500/10 text-blue-400 border border-blue-500/30"
                >
                  {t}
                </span>
              ))}
            </div>
          )}
        </div>
        {action && (
          <div onClick={(e) => e.stopPropagation()}>{action}</div>
        )}
      </div>
    </button>
  );
};

const ChatListPanel = ({ chats, activeChatId, onSelect, loading, emptyText, getAction }) => {
  const t = useT();
  const [online, setOnline] = useState({});
  const userIds = useMemo(
    () => [...new Set(chats.map((c) => c.user?.id).filter(Boolean))].slice(0, 100).sort((a, b) => a - b).join(","),
    [chats],
  );

  useEffect(() => {
    if (!userIds) return undefined;
    let cancelled = false;
    const load = () =>
      getPresence(userIds.split(","))
        .then((res) => {
          if (cancelled) return;
          const map = {};
          Object.entries(res?.users || {}).forEach(([id, p]) => { map[id] = !!p.is_online; });
          setOnline(map);
        })
        .catch(() => {});
    load();
    const timer = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [userIds]);

  return (
    <div className="h-full overflow-y-auto">
      {loading && chats.length === 0 ? (
        <div className="p-8 text-center text-text-secondary">{t("app.loading")}</div>
      ) : chats.length === 0 ? (
        <div className="p-8 text-center text-text-secondary">{emptyText || t("list.empty")}</div>
      ) : (
        chats.map((c) => (
          <ChatItem
            key={c.chat_id}
            chat={c}
            isActive={c.chat_id === activeChatId}
            onClick={() => onSelect(c.chat_id)}
            action={getAction ? getAction(c) : null}
            online={!!online[String(c.user?.id)]}
          />
        ))
      )}
    </div>
  );
};

export default ChatListPanel;
